import { useId, type ReactNode } from 'react';
import { cx } from './cx.js';

/**
 * A labelled control. Eleven inputs across the configuration pages were
 * placeholder-only, so the label vanished the moment you typed — and screen
 * readers never had one at all. Field owns the id wiring: the label points at
 * the control, hint and error are announced through aria-describedby, and an
 * error sets aria-invalid.
 *
 * The control is a render prop rather than a child so the aria attributes reach
 * whatever element is actually focusable (an <input>, a <select>, or the
 * Combobox's inner input).
 */
export interface FieldAria {
  id: string;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: true | undefined;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode | undefined;
  error?: ReactNode | undefined;
  required?: boolean | undefined;
  className?: string | undefined;
  children: (aria: FieldAria) => ReactNode;
}) {
  const base = useId();
  const hintId = hint ? `${base}-hint` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cx('ui-field', className)}>
      <label className="ui-field__label" htmlFor={base}>
        {label}
        {required ? (
          <span className="ui-field__req" title="required">
            *
          </span>
        ) : null}
      </label>
      {children({
        id: base,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {hint ? (
        <p className="ui-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        <p className="ui-field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
